import {
  createIssue,
  CreateIssueData,
  CreateIssueResponse,
  deleteIssuesById,
  DeleteIssuesByIdData,
  DeleteIssuesByIdResponse,
  getConfiguration,
  GetConfigurationResponse,
  getDefaultIcons,
  GetDefaultIconsResponse,
  getIssueById,
  GetIssueByIdData,
  GetIssueByIdResponse,
  IssueResponse,
  listIcons,
  ListIconsData,
  ListIconsResponse,
  listIssues,
  ListIssuesData,
  ListIssuesResponse,
  updateIssuesById,
  UpdateIssuesByIdData,
  UpdateIssuesByIdResponse,
} from '@data-access/connect/tc-issue-management';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';

// Constants for cache times (in milliseconds)
const ISSUES_LIST_STALE_TIME = 300000; // 5 minutes

// Default page size for issue list queries. Consumers can override via options.query.pageSize.
const DEFAULT_ISSUES_PAGE_SIZE = 500;
// Explicit maximum page size for queries designed to fetch all issues at once.
const MAX_ISSUES_PAGE_SIZE = 500;

// Constants for query key lengths
const QUERY_KEY_LENGTH_LIST = 2; // ['issues', contextId]
const QUERY_KEY_LENGTH_DETAIL = 3; // ['issues', contextId, issueId]

export class IssueApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  // Query for listing issues (paginated)
  public listIssuesQuery(
    contextId: Accessor<string | undefined>,
    options?: Partial<ListIssuesData>
  ): UseQueryResult<ListIssuesResponse, Error> {
    return useQuery(() => ({
      queryKey: ['issues', 'list', contextId(), options?.query],
      queryFn: async (): Promise<ListIssuesResponse> => {
        const context = contextId();
        if (!context) {
          throw new Error('Context ID is required');
        }

        const response = await listIssues({
          client: this.client,
          path: { contextId: context },
          query: {
            pageSize: DEFAULT_ISSUES_PAGE_SIZE,
            ...options?.query,
          },
        });

        if (response.error) {
          throw new Error('Failed to list issues');
        }

        return response.data;
      },
      enabled: !!contextId(),
    }));
  }

  // Query for listing all issues with maximum page size (500). This is not used in the UI.
  public listAllIssuesQuery(
    contextId: Accessor<string | undefined>,
    options?: Partial<ListIssuesData>
  ): UseQueryResult<ListIssuesResponse, Error> {
    return useQuery(() => ({
      queryKey: ['issues', 'list-all', contextId(), options?.query],
      queryFn: async (): Promise<ListIssuesResponse> => {
        const context = contextId();
        if (!context) {
          throw new Error('Context ID is required');
        }

        const response = await listIssues({
          client: this.client,
          path: { contextId: context },
          query: {
            pageSize: MAX_ISSUES_PAGE_SIZE,
            ...options?.query,
          },
        });

        if (response.error) {
          throw new Error('Failed to list all issues');
        }

        return response.data;
      },
      enabled: !!contextId(),
    }));
  }

  // Query for fetching all issues across multiple pages
  public listAllIssuesPaginatedQuery(
    contextId: Accessor<string | undefined>,
    options?: Partial<ListIssuesData>
  ): UseQueryResult<{ items: IssueResponse[]; totalCount: number }, Error> {
    return useQuery(() => ({
      queryKey: ['issues', 'list-all-paginated', contextId()],
      queryFn: async (): Promise<{
        items: IssueResponse[];
        totalCount: number;
      }> => {
        const context = contextId();
        if (!context) {
          throw new Error('Context ID is required');
        }

        const allItems: unknown[] = [];
        let skipToken: string | undefined;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await listIssues({
            client: this.client,
            path: { contextId: context },
            query: {
              pageSize: DEFAULT_ISSUES_PAGE_SIZE,
              skipToken,
              ...options?.query,
            },
          });

          if (response.error) {
            throw new Error('Failed to fetch issues page');
          }

          const data = response.data;

          // Add items from current page
          if (data.items && Array.isArray(data.items)) {
            allItems.push(...(data.items as IssueResponse[]));
          }

          // Check if there are more pages
          hasMorePages = !!data.links?.next;

          // Extract skipToken from next page URL if available
          if (data.links?.next?.href) {
            const url = new URL(data.links.next.href);
            skipToken = url.searchParams.get('skipToken') || undefined;
          }
        }

        return {
          items: allItems as IssueResponse[],
          totalCount: allItems.length,
        };
      },
      enabled: !!contextId(),
      staleTime: ISSUES_LIST_STALE_TIME, // 5 minutes - since we're fetching all data, cache it longer
    }));
  }

  // Query for getting a single issue by ID
  public getIssueByIdQuery(
    contextId: Accessor<string | undefined>,
    issueId: Accessor<string | undefined>,
    options?: Partial<GetIssueByIdData>
  ): UseQueryResult<GetIssueByIdResponse, Error> {
    return useQuery(() => ({
      queryKey: ['issues', contextId(), issueId(), options],
      queryFn: async (): Promise<GetIssueByIdResponse> => {
        const context = contextId();
        const issue = issueId();
        if (!context || !issue) {
          throw new Error('Context ID and Issue ID are required');
        }

        const response = await getIssueById({
          client: this.client,
          path: { contextId: context, issueId: issue },
          ...options,
        });

        if (response.error) {
          throw new Error('Failed to get issue');
        }

        return response.data;
      },
      enabled: !!contextId() && !!issueId(),
    }));
  }

  // Mutation for creating a new issue
  public createIssueMutation(): UseMutationResult<
    CreateIssueResponse,
    Error,
    CreateIssueData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: CreateIssueData
      ): Promise<CreateIssueResponse> => {
        const response = await createIssue({
          client: this.client,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to create issue');
        }

        return response.data;
      },
      onSuccess: () => {
        // Invalidate related queries
        void this.queryClient.invalidateQueries({ queryKey: ['issues'] });
      },
    }));
  }

  // Mutation for updating an existing issue
  public updateIssueMutation(): UseMutationResult<
    UpdateIssuesByIdResponse,
    Error,
    UpdateIssuesByIdData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: UpdateIssuesByIdData
      ): Promise<UpdateIssuesByIdResponse> => {
        const response = await updateIssuesById({
          client: this.client,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to update issue');
        }

        return response.data;
      },
      onSuccess: (_, variables) => {
        // Invalidate related queries
        void this.queryClient.invalidateQueries({ queryKey: ['issues'] });
        void this.queryClient.invalidateQueries({
          queryKey: [
            'issues',

            variables.path.contextId,

            variables.path.issueId,
          ],
        });
      },
    }));
  }

  // Mutation for deleting an issue
  public deleteIssueMutation(): UseMutationResult<
    DeleteIssuesByIdResponse,
    Error,
    DeleteIssuesByIdData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: DeleteIssuesByIdData
      ): Promise<DeleteIssuesByIdResponse> => {
        const response = await deleteIssuesById({
          client: this.client,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to delete issue');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Remove the specific deleted issue query from cache immediately
        // This prevents it from being refetched when we invalidate the list
        this.queryClient.removeQueries({
          queryKey: [
            'issues',

            variables.path.contextId,

            variables.path.issueId,
          ],
        });

        // Invalidate only the issues list queries, not the specific issue detail queries
        // This ensures the list refreshes but doesn't trigger a refetch of the deleted issue
        void this.queryClient.invalidateQueries({
          queryKey: ['issues'],
          // Predicate to only invalidate list queries (those with 2 items in queryKey)
          // List query key: ['issues', contextId]
          // Detail query key: ['issues', contextId, issueId, ...]
          predicate: (query) => {
            const key = query.queryKey;
            // Only invalidate if it's a list query (has exactly 2 items in key)
            // or if it's a different issue detail query (not the deleted one)
            if (
              Array.isArray(key) &&
              key.length === QUERY_KEY_LENGTH_LIST &&
              key[0] === 'issues'
            ) {
              return true; // Invalidate list queries
            }
            // Don't invalidate the deleted issue's detail query
            if (
              Array.isArray(key) &&
              key.length >= QUERY_KEY_LENGTH_DETAIL &&
              key[0] === 'issues' &&
              key[QUERY_KEY_LENGTH_LIST] === variables.path.issueId
            ) {
              return false; // Don't invalidate the deleted issue's query
            }
            return true; // Invalidate other issue queries
          },
        });
      },
    }));
  }

  // Query for getting issue configuration
  public getConfigurationQuery(
    containerId: Accessor<string | undefined>
  ): UseQueryResult<GetConfigurationResponse, Error> {
    return useQuery(() => ({
      queryKey: ['issue-configuration', containerId()],
      queryFn: async (): Promise<GetConfigurationResponse> => {
        const container = containerId();
        if (!container) {
          throw new Error('Container ID is required');
        }

        const response = await getConfiguration({
          client: this.client,
          path: { contextId: container },
        });

        if (response.error) {
          throw new Error('Failed to get configuration');
        }

        return response.data!;
      },
      enabled: !!containerId(),
    }));
  }

  // Query for listing custom icons within a specified context
  public listIconsQuery(
    contextId: Accessor<string | undefined>,
    options?: Partial<ListIconsData>
  ): UseQueryResult<ListIconsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['issues', 'icons', contextId(), options?.query],
      queryFn: async (): Promise<ListIconsResponse> => {
        const context = contextId();
        if (!context) {
          throw new Error('Context ID is required');
        }

        const response = await listIcons({
          client: this.client,
          path: { contextId: context },
          query: {
            pageSize: 200, // Maximum allowed page size
            ...options?.query,
          },
        });

        if (response.error) {
          throw new Error('Failed to list icons');
        }

        return response.data!;
      },
      enabled: !!contextId(),
    }));
  }

  // Query for getting default icons
  public getDefaultIconsQuery(): UseQueryResult<
    GetDefaultIconsResponse,
    Error
  > {
    return useQuery(() => ({
      queryKey: ['issues', 'icons', 'defaults'],
      queryFn: async (): Promise<GetDefaultIconsResponse> => {
        const response = await getDefaultIcons({
          client: this.client,
        });

        if (response.error) {
          throw new Error('Failed to get default icons');
        }

        return response.data!;
      },
    }));
  }

  /**
   * Fetches all issues in a context using the snapshot endpoint.
   * This is an imperative method for bulk fetching (not reactive).
   * Handles pagination automatically to fetch all issues.
   *
   * @param contextId - The context ID (e.g., trn:2:tc:us:projects:{projectId})
   * @param options - Optional parameters like fields to include
   * @returns All issues in the context
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 17 > 15 allowed; refactor later
  public async fetchIssuesSnapshot(
    contextId: string,
    options?: { fields?: string[] }
  ): Promise<any> {
    const allItems: IssueResponse[] = [];
    let skipToken: string | undefined;
    let changeToken: string | undefined;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await this.client.get<any>({
        url: `/context/${encodeURIComponent(contextId)}/issues/snapshot`,
        query: {
          pageSize: 1000, // Maximum allowed
          ...(skipToken && { skipToken }),
          ...(options?.fields && { fields: options.fields.join(',') }),
        },
      });

      if (response.error) {
        throw new Error('Failed to fetch issues snapshot');
      }

      const data = response.data;
      if (data?.items) {
        allItems.push(...(data.items as IssueResponse[]));
      }

      // Store the changeToken from the last response
      if (data?.changeToken) {
        changeToken = data.changeToken;
      }

      // Check if there are more pages - only continue if we can extract a valid skipToken
      hasMorePages = false;
      if (data?.links?.next?.href) {
        const href = data.links.next.href;
        const queryString = href.includes('?') ? href.split('?')[1] : '';
        const params = new URLSearchParams(queryString);
        const nextSkipToken = params.get('skipToken');
        if (nextSkipToken) {
          skipToken = nextSkipToken;
          hasMorePages = true;
        }
      }
    }

    return { items: allItems, changeToken };
  }

  /**
   * Fetches all issue links in a context using the snapshot endpoint.
   * This is an imperative method for bulk fetching (not reactive).
   * Returns all links with their associated issueId.
   * Handles pagination automatically to fetch all links.
   *
   * @param contextId - The context ID (e.g., trn:2:tc:us:projects:{projectId})
   * @returns All issue links in the context
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 16 > 15 allowed; refactor later
  public async fetchLinksSnapshot(contextId: string): Promise<any> {
    const allItems: Array<unknown> = [];
    let skipToken: string | undefined;
    let changeToken: string | undefined;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await this.client.get<any>({
        url: `/context/${encodeURIComponent(contextId)}/issues/links/snapshot`,
        query: {
          pageSize: 1000, // Maximum allowed
          ...(skipToken && { skipToken }),
        },
      });

      if (response.error) {
        throw new Error('Failed to fetch links snapshot');
      }

      const data = response.data;
      if (data?.items) {
        allItems.push(...data.items);
      }

      // Store the changeToken from the last response
      if (data?.changeToken) {
        changeToken = data.changeToken;
      }

      // Check if there are more pages - only continue if we can extract a valid skipToken
      hasMorePages = false;
      if (data?.links?.next?.href) {
        const href = data.links.next.href;
        const queryString = href.includes('?') ? href.split('?')[1] : '';
        const params = new URLSearchParams(queryString);
        const nextSkipToken = params.get('skipToken');
        if (nextSkipToken) {
          skipToken = nextSkipToken;
          hasMorePages = true;
        }
      }
    }

    return { items: allItems, changeToken };
  }
}
